/* eslint-disable react/jsx-handler-names */
import PropTypes from "prop-types";
import React, { useState } from "react";
import CustomImage from "../../../atoms/customImage";
import MenuItems from "./menuItems";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import useAnalytics from "../../../../analytics/useAnalytics";
import { pageName, areUrlsSame } from "../../../../utils/common";
import { usePathname } from "next/navigation";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { setIsNavigatingAction } from "../../../../store/slices/commonSlice";

/**
 * MegaMenu Component
 * @param {Object} props - The props object
 * @param {Array} props.menuItems - Array of menu items
 * @returns {JSX.Element|null} - Returns the MegaMenu JSX or null if no menu items provided
 */
const MegaMenu = ({ menuItems = [], isTopBanner }) => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(null);
  const pathname = usePathname();
  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");
  const { product } = useSelector(
    (state) => ({
      product: state?.pdp?.clientCurrentClickedItem,
    }),
    shallowEqual,
  );
  const urlIdentifier = product?.urlIdentifier;

  const megaMenuData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    action_type: "menu click",
  };

  if (!menuItems || menuItems.length === 0) {
    return null;
  }

  /**
   * Handles mouse enter event on menu category
   * Prefetches the route for faster navigation
   * @param {string} categoryName - The name of the category
   * @param {string} categoryId - The product category ID for prefetching
   */
  const onMouseEnter = (categoryName) => {
    setShowMenu(categoryName);
  };

  /**
   *
   * Handles mouse leave event on menu
   */
  const onMouseLeave = () => {
    setShowMenu(null);
  };

  /**
   * Handles menu item click - shows loader and tracks event
   * Loader will remain visible until pathname changes (page navigation completes)
   * Only shows loader if navigating to a different page
   * @param {Object} category - The category object
   * @param {Event} event - The click event
   */
  const handleMenuClick = (category, event) => {
    const categoryUrl = `/${category.productCategoryId}`;

    // Close the menu when clicking on a menu item
    setShowMenu(null);

    // If navigating to the same page, prevent navigation
    if (areUrlsSame(pathname, categoryUrl)) {
      if (event) {
        event.preventDefault();
      }
      return;
    }

    // Only show loader if navigating to a different page
    dispatch(setIsNavigatingAction(true));

    // Track event
    trackEvent(
      cleverTapCommonEvents.megaMenuClicked,
      {
        ...megaMenuData,
        menu_name: category?.categoryName,
        menu_category: category?.productCategoryId,
        image_name: category?.imageSource,
        image_id: category?.imageSource,
        is_having_image: !!category?.banners?.length,
      },
      ["clevertap"],
    );
    // Loader will automatically hide when pathname changes (detected in useEffect)
  };

  /** */
  const subMenuItemClicked = (categoryItem, categoryName) => {
    trackEvent(
      cleverTapCommonEvents.megaMenuClicked,
      {
        ...megaMenuData,
        menu_name: categoryName,
        sub_menu_name: categoryItem,
      },
      ["clevertap"],
    );
  };

  /**
   * Renders a single main menu item as a clickable link with tracking and a chevron icon.
   * @param {Object} category - The category object containing details for rendering the menu item.
   */
  const renderMainMenu = (category) => {
    const categoryUrl = `/${category.productCategoryId}`;

    return (
      <span className="inline-block cursor-pointer overflow-visible whitespace-nowrap font-inter text-14 text-grey-300">
        <a
          href={categoryUrl || "#"}
          className="flex items-center justify-center gap-4"
          role="button"
          aria-label={category.categoryName}
          itemProp="url"
          onClick={(e) => {
            handleMenuClick(category, e);
          }}
        >
          <span
            className={`text-12 font-500 text-black-900 hover:text-green-100 xl:text-14 ${category?.dataStyle ? category?.dataStyle : ""}`}
          >
            {category.categoryName}
          </span>
          <span
            className={`h-10 w-10 transition-all duration-500 ${showMenu === category.categoryName ? "rotate-180" : "rotate-360"}`}
          >
            <CustomImage src="/icons/navigation-chevron.svg" alt="down-arrow" height={100} width={100} sizes="10vw" />
          </span>
        </a>
      </span>
    );
  };

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 right-0 ${isTopBanner ? "top-[182px] xml:top-[190px] xmll:top-[195px] xxl:top-[205px]" : "top-[139px]"} z-40 bg-black-900 transition-opacity duration-500 ${
          showMenu ? "opacity-60" : "pointer-events-none opacity-0"
        }`}
        onClick={onMouseLeave}
      />

      {menuItems?.map((category) =>
        category?.childCategoryList?.length > 0 ? (
          <nav
            key={`${category.categoryName}${category.parentProductCategoryId}`}
            className="relative z-50 flex py-2 before:absolute before:-bottom-12 before:z-1 before:h-[2px] before:w-full before:content-[''] hover:before:bg-green-100"
            onMouseLeave={onMouseLeave}
            onMouseEnter={() => onMouseEnter(category.categoryName)}
            id="nav_menu_items"
            aria-label={category.categoryName || "Main menu"}
          >
            {renderMainMenu(category)}
            <div className="fixed left-0 right-0 mx-40 mt-[31px] xxl:mt-34">
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out
                ${showMenu === category.categoryName ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}`}
              >
                {
                  <MenuItems
                    category={category?.childCategoryList}
                    imageBanners={category?.banners}
                    onMenuItemClick={() => setShowMenu(null)}
                    subMenuItemClicked={subMenuItemClicked}
                    categoryName={category?.categoryName}
                  />
                }
              </div>
            </div>
          </nav>
        ) : (
          <nav
            key={`${category.categoryName}${category.parentProductCategoryId}`}
            className="relative z-50 flex py-2 before:absolute before:-bottom-12 before:z-1 before:h-[2px] before:w-full before:content-[''] hover:before:bg-green-100"
            id="nav_menu_items"
            aria-label={category.categoryName || "Main menu"}
          >
            {renderMainMenu(category)}
          </nav>
        ),
      )}
    </>
  );
};

MegaMenu.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      categoryName: PropTypes.string,
      productCategoryId: PropTypes.string,
      childCategoryList: PropTypes.array,
    }).isRequired || PropTypes.array,
  ).isRequired,
  isTopBanner: PropTypes.bool,
};

export default React.memo(MegaMenu);
